/**
 * @file mail-types.ts
 * @description Types et types pour le système de gestion des emails.
 * Ce fichier définit la structure de données utilisée pour représenter les emails
 * et leurs composants, avec support pour les fonctionnalités d'IA.
 */

import type { IDateValue } from "../../../utils";
import { CategoryRepartition } from "../../customView/types/ui";

export type MailIds = {
    id: string;
    internetMessageId: string;
};

// #region TYPES DE BASE
/**
 * Types simples et unions utilisés dans l'application
 */

/**
 * Format de contenu possible pour un email (utilisé pour l'envoi/réponse)
 */
type ContentType = "Text" | "HTML";

/**
 * Type de contenu du body d'un email (utilisé pour les échanges avec l'API backend)
 * Valeurs lowercase alignées avec le backend (BodyContentType Zod enum)
 */
enum BodyContentType {
    TEXT = "text",
    HTML = "html",
}

/**
 * Énumération des tailles possibles pour les composants d'email
 * Le format est largeurxhauteur en unités de grille
 */
enum MailComponentSize {
    /** Composant de taille 1x1 (standard, carré) */
    SMALL = "1x1",

    /** Composant de taille 1x2 (étroit et haut) */
    NARROW = "1x2",

    /** Composant de taille 2x1 (large et court) */
    WIDE = "2x1",

    /** Composant de taille 2x2 (grand, double largeur et hauteur) */
    LARGE = "2x2",
}

// #endregion

// #region types PRINCIPAUX DES EMAILS

/**
 * Label qui peut être attribué à un email
 * Les labels permettent de catégoriser et de filtrer les emails
 */
type MailLabel = {
    /** Identifiant unique du label */
    id: string;
    /** Type de label (ex: 'system', 'custom') */
    type: string;
    /** Nom affiché du label */
    name: string;
    /** Code couleur au format HEX */
    color: string;
    /** Nombre d'emails non lus avec ce label */
    unreadCount?: number;
};

/**
 * Représente un expéditeur ou destinataire d'email
 */
type MailSender = {
    /** Nom complet de la personne */
    name: string;
    /** Adresse email */
    email: string;
    /** URL de l'avatar, null si aucun avatar disponible */
    avatarUrl: string | null;
};

/**
 * Pièce jointe attachée à un email
 */
type MailAttachment = {
    /** Identifiant unique de la pièce jointe */
    id: string;
    /** Nom du fichier */
    name: string;
    /** Taille en octets */
    size: number;
    /** Type MIME du fichier */
    type: string;
    /** Chemin d'accès au fichier */
    path: string;
    /** URL de prévisualisation */
    preview: string;
    /** Content-ID (RFC 2392) — identifiant de CONTENU, stable et identique entre un mail et sa copie
     *  transférée (insensible à la réécriture du conteneur par Exchange, contrairement à `size`).
     *  Sert de clé de déduplication fiable du panneau Documents. Absent pour certaines PJ. Cf. AB#1681. */
    contentId?: string;
    /** Date de création */
    createdAt?: IDateValue;
    /** Date de dernière modification */
    modifiedAt: IDateValue;
    /** Date d'envois en pièce jointe */
    sentAt: IDateValue;
    /** PJ intégrée au corps (CID) : exclue des documents d'une CustomView (AB#1727). Transite en round-trip
     *  via le payload d'analyse (spread complet côté Backend, pas d'allowlist) ; typé ici pour verrouiller le
     *  contrat plutôt que de la laisser transiter en propriété runtime non typée (review PR #210). */
    isInline?: boolean;
};

/**
 * Métadonnées LÉGÈRES d'une pièce jointe (sans la preview, trop lourde).
 * Fournies par le Backend pour lister l'intégralité des documents d'une CustomView sans charger les mails.
 * La preview (data URL base64) se demande ensuite à la demande via `MailManager.getAttachmentPreview(s)`.
 */
type AttachmentMetadata = {
    /** Identité de la COPIE porteuse = `${internetMessageId}::${parentFolderId}` (convention interface FrontEnd,
     *  décision figée #6 « un seul id partout »). Sert à re-demander la preview. Techniquement l'internetMessageId
     *  suffirait (PJ identique entre copies), mais toutes les interfaces exposées au Front utilisent uniqueMailvistaId
     *  pour lever l'ambiguïté entre les 2 copies d'un self-sent (Inbox vs SentItems). */
    uniqueMailvistaId: string;
    /** Identifiant Graph de la pièce jointe (scope de la preview au sein du mail) */
    attachmentId: string;
    /** Nom du fichier */
    name: string;
    /** Taille en octets */
    size: number;
    /** Type MIME du fichier */
    type: string;
    /** Content-ID (RFC 2392) — clé de dédup stable (dédup déjà faite côté Backend). Cf. AB#1681. */
    contentId?: string;
    /** Date d'envoi en pièce jointe (ISO 8601) */
    sentAt: string;
};

/**
 * Référence d'une pièce jointe pour récupérer sa preview à la demande.
 * `uniqueMailvistaId` = `${internetMessageId}::${parentFolderId}` (convention interface FrontEnd).
 */
type AttachmentPreviewRef = {
    uniqueMailvistaId: string;
    attachmentId: string;
};

/**
 * Preview d'une pièce jointe résolue, reliée à sa référence.
 */
type AttachmentPreview = {
    uniqueMailvistaId: string;
    attachmentId: string;
    /** URL de prévisualisation (ex: `data:<mime>;base64,...`) */
    preview: string;
};

/**
 * Contenu « widgets » d'UN mail : ses pièces jointes (métadonnées légères), liens et événements.
 * Unité de cache du Record par-mail (clé = internetMessageId) alimentant l'affichage docs/links/events
 * d'une CustomView, indépendamment de la pagination. Données BRUTES par mail : la dédup cross-mail
 * (attachments par contentId, links par URL) et le filtrage des events passés sont faits au READ
 * (agrégation view-level dans CustomViewManager).
 */
// NB: champs `readonly` — c'est un READ-MODEL de cache. Les lectures (getMailViewContentFromRecord)
// renvoient les objets DU cache : les typer readonly documente le contrat « ne pas muter » (une mutation
// côté consommateur muterait le Record). Les lecteurs CustomView renvoient, eux, des tableaux NEUFS (dédup).
type MailViewContent = {
    /** Pièces jointes (sans preview) */
    readonly attachments: readonly AttachmentMetadata[];
    /** Liens trouvés dans le corps du mail */
    readonly links: readonly LinkObject[];
    /** Événements liés (invitations) — bruts, non filtrés sur le temps */
    readonly events: readonly LinkedEventDetails[];
};

/**
 * Détails d'un email
 * Représentation complète d'un email dans le système
 */
type Mail = {
    /** Identifiant unique interne */
    id: string;
    /** Identifiant du message Internet unique (format RFC 5322) */
    internetMessageId: string;
    /**
     * Identité de la COPIE physique = `${internetMessageId}::${parentFolderId}` (Q-F1, modèle multi-copies).
     * Calculé/stampé par le SDK au point d'écriture du Record (`_setMailInRecord`, re-stampé au move dans
     * `_updateRecordWithoutAnalysis`) ⇒ fiablement présent sur tout mail rendu au FrontEnd (listes + events).
     * Optionnel au niveau du type pour ne pas contraindre tous les producteurs bruts de `Mail` (Graph/API).
     * Le FrontEnd l'utilise pour router les mutations copie-scopées (cf. Doc/identite-mail/14 WS-F3).
     */
    uniqueMailvistaId?: string;
    /** Identifiant de conversation */
    conversationId: string;
    /** Dossier contenant l'email */
    parentFolderId: string;
    /** Sujet de l'email */
    subject: string;
    /** Contenu complet du message */
    message: string;
    /** Contenu du message sans les réponses précédentes*/
    uniqueBody: string;
    /** Aperçu court du message */
    messagePreview: string;
    /** DEPRECATED - Indique si l'email n'a pas été lu */
    // isUnread: boolean;
    /** Indique si l'email a été lu */
    isRead: boolean;
    /** Expéditeur de l'email */
    from: MailSender;
    /** Liste des destinataires */
    to: MailSender[];
    /** Liste des destinataires copie */
    cc: MailSender[];
    /** Liste des destinataires copie */
    bcc: MailSender[];
    /** Liste des identifiants de labels associés */
    labelIds: string[];
    /** Indique si l'email est marqué comme favori */
    isStarred: boolean;
    /** Indique si l'email est marqué comme important */
    isImportant: boolean;
    /** Date et heure d'envoi */
    sentDateTime: IDateValue;
    /** Liste des pièces jointes */
    attachments: MailAttachment[];
    /** Indique si l'email a des pièces jointes */
    hasAttachment: boolean;
    /** Indique si l'utilisateur a traité cet email */
    isUserProcessed: boolean;
    /** Informations générées par l'IA (si disponible) */
    aiDetails?: MailAIDetails;
    /** Détails d'un événement lié (si l'email est une invitation) */
    linkedEventDetails?: LinkedEventDetails;
    /** Indique si l'email référence une réunion */
    hasLinkedMeeting: boolean;
    /** Score de classement calculé pour l'email (optionnel) */
    _ranking?: MailScore;
    /** Hash du context utilisé pour le calcul du ranking (optionnel) */
    _rankingContextHash?: string;
    /** Taille du composant mail dans l'affichage Mosaique */
    componentSize: MailComponentSize;
    /** Indique si le mail est focus */
    isFocus: boolean;
    /** SmartLabel si il en possède un */
    smartLabel?: SmartLabel;
    /** Links found in mail unique body */
    links: LinkObject[];
    /** Indique si un mail a été manipulé par le user */
    isUserEdited: boolean;
    /** Score du mail après analyse */
    score: number | null;
    /** Date ISO du dernier déplacement manuel vers Focus (RG2). null si focus auto ou hors Focus. */
    manualFocusDate: string | null;
    /** Date ISO copiée de sentDateTime si l'expéditeur est Mailvista, sinon null. Clé tri Focus groupe 1. */
    sentDateTimeMailvista: string | null;
    /** Type de contenu du body principal (text ou html) */
    bodyContentType: BodyContentType;
    /** Type de contenu du uniqueBody — body nettoyé sans les réponses précédentes (text ou html) */
    uniqueBodyContentType: BodyContentType;
};

type MailScore = {
    /** Score total de l'email (0-100) */
    totalScore: number;

    /** Détail des scores par dimension de calcul */
    detailedScores: {
        /** Score basé sur les correspondances de personnes (après pondération) */
        personnesScore: number;
        /** Score basé sur les correspondances de titres/contenus (après pondération) */
        titresScore: number;
        /** Score basé sur les correspondances d'équipes/projets (après pondération) */
        teamsScore: number;
        /** Score basé sur la présence de mon mail dans dest ou cc */
        addresseeScore: number;
        /** Score basé sur l'analyse de ton (après pondération) */
        toneScore?: number;
    };

    completeDetailedScores: any;
};

/** Chronological ordered list of thread mails */
type MailThread = Mail[];

// #endregion

// #region types POUR L'ENVOI D'EMAILS

/**
 * Pièce jointe pour l'envoi d'un nouvel email
 */
type Attachment = {
    /** Contenu du fichier encodé en Base64 */
    contentBytes: string;
    /** Nom du fichier */
    name: string;
    /** Type MIME (ex: 'application/pdf', 'image/jpeg') */
    contentType: string;
    /** Taille en octets (optionnel) */
    size?: number;
    /** Indique si la pièce jointe est intégrée dans le corps de l'email (pour les images) */
    isInline?: boolean;
    /** Identifiant unique pour référencer la pièce jointe dans le contenu HTML (utilisé avec isInline) */
    contentId?: string;
};

/**
 * Structure pour l'envoi d'un nouvel email
 */
type MailSend = {
    /** Sujet de l'email */
    subject: string;
    /** Corps du message */
    content: string;
    /** Liste des adresses email des destinataires principaux */
    toRecipients: string[];
    /** Liste des adresses email en copie */
    ccRecipients: string[];
    /** Liste des adresses email en copie carbone */
    bccRecipients: string[];
    /** Format du contenu (texte ou HTML) */
    contentType?: ContentType;
    /** Pièces jointes à inclure */
    attachments?: Attachment[];
};

/**
 * Structure pour répondre ou transférer un email
 */
type ReplyForward = {
    /** Sujet du message transféré */
    subject?: string;
    /** Corps du message de réponse ou transfert */
    content: string;
    /** Destinataires (optionnel, utilisé principalement pour le transfert) */
    toRecipients?: string[];
    /** Destinataires copie (optionnel, utilisé principalement pour le transfert) */
    ccRecipients?: string[];
    /** Destinataires copie carbone (optionnel, utilisé principalement pour le transfert) */
    bccRecipients?: string[];
    /** Format du contenu (texte ou HTML) */
    contentType?: ContentType;
    /** Pièces jointes additionnelles */
    attachments?: Attachment[];
};

// #endregion

// #region types POUR LES ÉVÉNEMENTS LIÉS

/**
 * Localisation d'un événement
 */
type EventLocation = {
    /** Nom affiché du lieu */
    displayName: string;
    /** Type de lieu (physique, virtuel, etc.) */
    locationType: string;
    /** Type d'identifiant unique utilisé */
    uniqueIdType: string;
};

enum AttendeeResponseStatus {
    /** Organiser point of view : user did not responded */
    NONE = "none",
    /** Organier point of view : user created the event, no response needed */
    ORGANIZER = "organizer",
    /** User point of view : user tentatively accepted */
    TENTATIVELY_ACCEPTED = "tentativelyAccepted",
    /** User point of view : user accepted */
    ACCEPTED = "accepted",
    /** User point of view : user declined*/
    DECLINED = "declined",
    /** User point of view : user did not responded */
    NOT_RESPONDED = "notResponded",
}

enum EventMessageType {
    EVENT_MESSAGE_REQUEST = "Request",
    EVENT_MESSAGE_RESPONSE = "Response",
    EVENT_MESSAGE = "Message",
}

enum EventMessageResponseType {
    NONE = "none",
    TENTATIVELY_ACCEPTED = "tentativelyAccepted",
    ACCEPTED = "accepted",
    DECLINED = "declined",
}

/**
 * Détails d'un événement lié à un email
 * Utilisé principalement pour les invitations calendrier
 */
type LinkedEventDetails = {
    /** Id de l'event */
    id: string;
    /** Type de message de réunion */
    meetingMessageType: string;
    /** Précision sur le type de demande */
    meetingRequestType: string;
    /** Sujet de l'événement */
    subject: string;
    /** Prévisualisation du contenu de l'evenement */
    bodyPreview: string;
    /** Date et heure de début */
    startDateTime: IDateValue;
    /** Date et heure de fin */
    endDateTime: IDateValue;
    /** Indique si l'événement dure toute la journée */
    isAllDay: boolean;
    /** Informations de récurrence (si l'événement est récurrent) */
    recurrence: any; // TODO: Définir une type précise après tests
    /** Indique si une réponse est demandée - utilisé par le Front pour afficher la carte */
    responseRequested: boolean;
    /** Indique si l'événement est délégué */
    isDelegated: boolean;
    /** Localisation de l'événement */
    location: EventLocation;
    /** Statut de réponse de l'utilisateur courant */
    userResponseStatus: AttendeeResponseStatus;
    /** Indique si l'événement est expiré */
    isExpired: boolean;
    /** Indique si l'événement est annulé */
    isCancelled: boolean;
    /** Type de message d'événement */
    messageType: EventMessageType;
    /** Type de réponse à un événement */
    responseType?: EventMessageResponseType;
    /** Mail de l'organisateur de l'evenement */
    organizerMail: string;
    /**
     * Date d'envoi (ISO 8601) du MAIL porteur de l'événement (= sentDateTime du mail), et non une date propre
     * à l'événement. Sert au FrontEnd à ordonner chronologiquement les copies d'une même réunion (invitation →
     * mise à jour → annulation) pour n'afficher que la plus récente dans une CustomView, où — contrairement au
     * thread — les mails ne sont pas chargés côté front (cf. AB#1727, Meetings / eventSentDateTime).
     *
     * Optionnel au niveau du type : les `mail.linkedEventDetails` issus de Graph (BaseMail) ne le portent pas.
     * En revanche il est REQUIS dans le contrat `POST /v1/mails/content` (chaque `events[]` d'un MailViewContent
     * doit le renseigner = sentDateTime du mail), au même titre que `AttachmentMetadata.sentAt`. Le SDK l'estampe
     * aussi sur le chemin write-through thread (`_seedMailViewContentFromMail`) pour une forme homogène.
     */
    sentAt?: string;
};

// #endregion

// #region types POUR L'ANALYSE IA (BABAYAGA)

// IaResponse is an alias for string
interface LinkObject {
    url: string;
    name: string;
}

/**
 * Tâche détectée par l'IA dans un email.
 * Type unique : toutes les routes todo servent cette forme, normalisée au bord du SDK.
 */
type TodoItem = {
    /** Identifiant unique et stable, décidé en amont de la DB */
    id: string;
    /** Mail auquel la tâche est rattachée */
    internetMessageId: string;
    /** Résumé de la tâche à effectuer */
    taskSummary: string;
    /** Identifiant d'un événement lié (si applicable) */
    linkedEventId?: string;
    /** Indique si la tâche est terminée */
    done: boolean;
    /** La tâche a été acceptée par l'utilisateur et alimente son dashboard */
    isUserAccepted: boolean;
    /** Date de validation (ISO string), null tant que la tâche n'est pas terminée */
    checkedDate: string | null;
};

/**
 * Entité détectée par l'IA dans un email
 * (ex: personne, organisation, lieu, date, etc.)
 */
type MailEntity = {
    /** Type d'entité (personne, organisation, lieu, etc.) */
    type: string;
    /** Contenu textuel de l'entité */
    content: string;
};

type ToneAnalysis = {
    imperious: number;
    urgency: number;
    directive: number;
    impact: number;
};

/**
 * Détails générés par l'IA pour un email
 * Contient diverses analyses et résumés générés automatiquement
 */
type MailAIDetails = {
    /** Résumé court du contenu de l'email */
    mailSummary: string;
    /** Résumé détaillé du contenu de l'email */
    longMailSummary: string;
    /** Catégories détectées pour cet email */
    categories: string[];
    /** Entités extraites du contenu de l'email */
    entities: MailEntity[];
    /** Date à laquelle l'email a été analysé par l'IA */
    analyzedMailDate: IDateValue;
    /** Mots-clés extraits du contenu */
    keywords: string[];
    /** Liste des tâches à faire détectées */
    toDo: TodoItem[];
    /** Analyse de ton de l'email */
    toneAnalysis: ToneAnalysis | undefined;
};

type MenulInfo = {
    color: string;
    title: string;
    icon: string;
};

// #endregion

export enum SmartLabel {
    Focus = "smart-label-focus",
    PourInfo = "smart-label-pour-info",
    Newsletter = "smart-label-newsletter",
    Suspect = "smart-label-suspect", // ⚠️ INACTIF — ne pas exposer, ne pas requêter (backend renvoie 400)
    Autre = "smart-label-autre",
    Traites = "smart-label-traites",
}

/** Labels actifs — les seuls à passer à GET /v1/smartLabels/:label. Suspect est délibérément absent. */
export const ACTIVE_SMART_LABELS: SmartLabel[] = [
    SmartLabel.Focus,
    SmartLabel.PourInfo,
    SmartLabel.Newsletter,
    SmartLabel.Autre,
    SmartLabel.Traites,
];

export const SmartLabelInfos: Record<SmartLabel, MenulInfo> = {
    [SmartLabel.Focus]: {
        color: "#FF86A4",
        title: "Focus",
        icon: "???",
    },
    [SmartLabel.PourInfo]: {
        color: "#86A2FF",
        title: "Pour info",
        icon: "???",
    },
    [SmartLabel.Newsletter]: {
        color: "#55DCCC",
        title: "Newsletter",
        icon: "???",
    },
    [SmartLabel.Suspect]: {
        color: "#D59976",
        title: "Suspect",
        icon: "???",
    },
    [SmartLabel.Autre]: {
        color: "#EBE8F4",
        title: "Autre",
        icon: "???",
    },
    [SmartLabel.Traites]: {
        color: "#EBE8F4",
        title: "Traités",
        icon: "???",
    },
};

export enum Categories {
    Important = "important",
    ReponseAttendue = "reponse_attendue",
    Rappel = "rappel",
    Informatif = "informatif",
    Newsletter = "newsletter",
    Suspect = "suspect",
    Autre = "autre",
}

type SmartLabelSummary = {
    count: number;
    categoryRepartition: CategoryRepartition[];
};

// Exports
export type {
    ContentType,
    MailLabel,
    MailSender,
    MailAttachment,
    AttachmentMetadata,
    AttachmentPreviewRef,
    AttachmentPreview,
    MailViewContent,
    MenulInfo,
    Mail,
    MailThread,
    MailScore,
    Attachment,
    MailSend,
    ReplyForward,
    EventLocation,
    LinkedEventDetails,
    TodoItem,
    MailEntity,
    MailAIDetails,
    SmartLabelSummary,
    LinkObject,
};

export {
    BodyContentType,
    MailComponentSize,
    AttendeeResponseStatus,
    EventMessageType,
    EventMessageResponseType,
};
